import React, { FC, useState, FormEvent } from 'react'
import { Alert } from 'components/Alert'
import { Stack } from 'components/Stack'
import { Heading } from 'components/Heading'
import { Text } from 'components/Text'
import { Card } from 'components/Card'
import { Button } from 'components/Button'
import { useTranslation } from 'react-i18next'
import { SelectOption, SelectInput } from 'components/SelectInput/SelectInput'
import { SelectOptionAdapter } from 'utils/selectOption'
import { Language } from 'models/language'

type LanguageFormProps = {
  language: Language
  languages: Language[]
  submit: (language: string) => Promise<void>
}

export const LanguageForm: FC<LanguageFormProps> = (props) => {
  const { t } = useTranslation()
  const [success, setSuccess] = useState(false)
  const [error, setError] = useState(false)
  const [loading, setLoading] = useState(false)

  const selectOptionAdapter = SelectOptionAdapter('code', 'label')
  const [language, setLanguage] = useState<SelectOption>(
    selectOptionAdapter.buildOption(props.language),
  )

  const handleSubmit = (event: FormEvent) => {
    event.preventDefault()
    setLoading(true)
    setError(false)
    setSuccess(false)

    props
      .submit(language.value.toString())
      .then(() => {
        setSuccess(true)
      })
      .catch(() => setError(true))
      .finally(() => setLoading(false))
  }

  return (
    <div>
      <Card>
        <Stack>
          <Stack space="small">
            <Heading size="card">{t('account.language.title')}</Heading>
            <Text>{t('account.language.subtitle')}</Text>
          </Stack>

          <form onSubmit={handleSubmit}>
            <Stack space="medium">
              <SelectInput
                label={t('account.language.form.language')}
                value={language}
                options={selectOptionAdapter.buildOptions(props.languages)}
                onChange={(language) => (language ? setLanguage(language) : null)}
              />

              <Button type="submit" loading={loading}>
                {t('account.language.form.cta')}
              </Button>
              {success && (
                <Alert tone="positive" full>
                  {t('common.messages.success')}
                </Alert>
              )}
              {error && (
                <Alert tone="critical" full>
                  {t('common.messages.error')}
                </Alert>
              )}
            </Stack>
          </form>
        </Stack>
      </Card>
    </div>
  )
}

LanguageForm.displayName = 'LanguageForm'
